import { useQuery } from '@tanstack/react-query';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { terraProxyApiClient } from '../../clients/testerClient';
import { toSurfaceSignalListQueryFromPortalFilters } from '../../clients/tester-client/types';
import { mapSurfaceSignalToGroupedSignal } from '../useSignals';
import { isAppTarget } from '../../utils/target-scope.utils';

export const TARGET_SIGNALS_QUERY_KEY = 'target-signals';

export async function listTargetSignalsForScope({
  targetId,
  targetType,
  limit = 100,
  skip,
  filters,
}: {
  targetId: string;
  targetType: TargetTypeType;
  limit?: number;
  skip?: number;
  filters?: Record<string, unknown>;
}): Promise<GroupedSignal[]> {
  if (isAppTarget(targetType)) {
    const result = await terraProxyApiClient.getSignalsByApplication(targetId, {
      pagination: { skip, limit },
      filters,
    });

    return result?.signals ?? [];
  }

  const surfaceQuery = toSurfaceSignalListQueryFromPortalFilters(filters);
  const result = await terraProxyApiClient.listSurfaceSignals({
    targetType,
    surfaceId: targetId,
    skip,
    limit,
    ...surfaceQuery,
  });

  return result.data.map((signal) => mapSurfaceSignalToGroupedSignal(signal));
}

export function useTargetSignals({
  targetId,
  targetType,
  enabled = true,
  limit = 100,
  skip,
  filters,
  queryKeySuffix,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  enabled?: boolean;
  limit?: number;
  skip?: number;
  filters?: Record<string, unknown>;
  queryKeySuffix?: string;
}) {
  return useQuery({
    queryKey: [
      TARGET_SIGNALS_QUERY_KEY,
      targetId,
      targetType,
      limit,
      skip,
      filters,
      queryKeySuffix,
    ],
    queryFn: () => {
      if (!targetId) {
        throw new Error('Missing targetId for target signals');
      }

      return listTargetSignalsForScope({
        targetId,
        targetType,
        limit,
        skip,
        filters,
      });
    },
    enabled: Boolean(targetId) && enabled,
    staleTime: 30000,
  });
}
